export enum FormFieldType {
  INPUT = 'input',
  SELECT = 'select',
  RADIO = 'radio',
  CHECKBOX = 'checkbox',
  FIELD_ARRAY = 'field_array',
  SCALE_RATING = 'scale_rating',
  HEADING = 'heading',
  TEXT = 'text',
  PAGE_BREAK = 'page_break',
}

// UI-only value used by choice controls; it is never submitted.
export const FORM_OTHER_OPTION_VALUE =
  '__form_other_option__:806ec086-450f-4e3f-9a2f-e49cd1db0068';

export enum FormChoiceResponseType {
  OPTION = 'option',
  OTHER = 'other',
}

export interface FormChoiceResponse {
  value: string;
  type: FormChoiceResponseType;
}

export interface InputOptions {
  maxValue?: number;
  minValue?: number;
  integer?: boolean;
  maxLength?: number;
  minLength?: number;
  type: 'text' | 'email' | 'number';
}

export interface SelectOptions {
  options: string[];
  multiple?: boolean;
  allowOther?: boolean;
  explanation?: string;
  correctOptions?: number[];
}

export interface RadioOptions {
  options: string[];
  allowOther?: boolean;
}

export interface CheckboxOptions {
  options: string[];
  allowOther?: boolean;
}

// API contract: one blank row denotes a single-question scale. A named row is a matrix.
export const FORM_SINGLE_SCALE_ROW_VALUE = '';

export interface ScaleRatingOptions {
  minValue: number;
  maxValue: number;
  options: string[];
  valueLabels?: string[];
}

export interface FieldArrayOptions {
  fields: FormField[];
  layout?: 'vertical' | 'horizontal';
}

export type FormFieldTranslation = {
  label: string;
  text?: string;
  subtitle?: string;
  placeholder?: string;
};

export type FormFieldTranslationWith<T> = FormFieldTranslation & T;

interface BaseFormField {
  id: string;
  label: string;
  required?: boolean;
  placeholder?: string;
}

export interface InputFormField extends BaseFormField {
  options?: InputOptions;
  type: FormFieldType.INPUT;
  translations?: Record<string, FormFieldTranslation>;
}

export interface RadioFormField extends BaseFormField {
  options?: RadioOptions;
  type: FormFieldType.RADIO;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<RadioOptions, 'options'>>
  >;
}

export interface CheckboxFormField extends BaseFormField {
  options?: CheckboxOptions;
  type: FormFieldType.CHECKBOX;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<CheckboxOptions, 'options'>>
  >;
}

export interface SelectFormField extends BaseFormField {
  options?: SelectOptions;
  type: FormFieldType.SELECT;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<SelectOptions, 'options' | 'explanation'>>
  >;
}

export interface ScaleRatingFormField extends BaseFormField {
  options?: ScaleRatingOptions;
  type: FormFieldType.SCALE_RATING;
  translations?: Record<
    string,
    FormFieldTranslationWith<
      Pick<ScaleRatingOptions, 'options' | 'valueLabels'>
    >
  >;
}

export interface FieldArrayFormField extends BaseFormField {
  options?: FieldArrayOptions;
  type: FormFieldType.FIELD_ARRAY;
}

export interface PageBreakFormField extends BaseFormField {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  options?: {};
  type: FormFieldType.PAGE_BREAK;
}

/** Static content ignores inherited answer settings such as required. */
export interface TextFormField extends Omit<BaseFormField, 'placeholder'> {
  text: string;
  type: FormFieldType.TEXT;
  options?: Record<string, never>;
  translations?: Record<
    string,
    Omit<FormFieldTranslation, 'subtitle' | 'placeholder'>
  >;
}

export interface HeadingFormField extends Omit<BaseFormField, 'placeholder'> {
  subtitle?: string;
  type: FormFieldType.HEADING;
  options?: Record<string, never>;
  translations?: Record<string, Omit<FormFieldTranslation, 'placeholder'>>;
}

export type FormField =
  | TextFormField
  | InputFormField
  | RadioFormField
  | SelectFormField
  | HeadingFormField
  | CheckboxFormField
  | PageBreakFormField
  | FieldArrayFormField
  | ScaleRatingFormField;

export interface FormBlock {
  id: string;
  name: string;
  fields: FormField[];
}

export interface FormData {
  blocks: FormBlock[];
}

export interface FormSubmissionData {
  [fieldId: string]:
    | string
    | number
    | string[]
    | number[]
    | FormChoiceResponse
    | FormSubmissionData
    | FormChoiceResponse[];
}

export interface FormSubmission {
  id: string;
  userId: string;
  formId: string;
  createdAt: string;
  updatedAt: string;
  data: FormSubmissionData;
}

export interface ValidationError {
  fieldId: string;
  message: string;
}

export interface Form {
  id: string;
  data: FormData;
  version: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  courseStepId: string | null;
}

export interface FormSubmissionPayload {
  formId: string;
  isSubmitted: boolean;
  data: FormSubmissionData;
}
